// frontend/src/components/public/blocks/VideoShowcaseBlock.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogContent
} from '@mui/material';
import {
  PlayCircle as PlayIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';

interface VideoShowcaseBlockProps {
  data: {
    heading: string;
    description?: string;
    layout: 'grid' | 'featured' | 'carousel';
    videos: Array<{
      url: string;
      title: string;
      description?: string;
      metricCallout?: string;
      category?: string;
      thumbnail?: string;
      ctaText?: string;
      ctaLink?: string;
    }>;
    showModal: boolean;
    autoplayFeatured: boolean;
    sectionCtaText?: string;
    sectionCtaLink?: string;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const VideoShowcaseBlock: React.FC<VideoShowcaseBlockProps> = ({ data, theme }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const {
    heading,
    description,
    layout = 'grid',
    videos = [],
    showModal = true,
    autoplayFeatured = false,
    sectionCtaText,
    sectionCtaLink
  } = data;

  // Extract video ID and type from URL
  const getVideoEmbedUrl = (url: string, autoplay: boolean = false) => {
    if (!url) return null;

    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (ytMatch) {
      const params = autoplay ? '?autoplay=1&mute=1' : '';
      return `https://www.youtube.com/embed/${ytMatch[1]}${params}`;
    }

    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      const params = autoplay ? '?autoplay=1&muted=1' : '';
      return `https://player.vimeo.com/video/${vimeoMatch[1]}${params}`;
    }

    return null;
  };

  // Get thumbnail URL
  const getVideoThumbnail = (video: any) => {
    if (video.thumbnail) return video.thumbnail;

    const ytMatch = video.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (ytMatch) {
      return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
    }

    const vimeoMatch = video.url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://vumbnail.com/${vimeoMatch[1]}.jpg`;
    }

    return '/placeholder-video.jpg';
  };

  const handleVideoClick = (index: number) => {
    if (showModal) {
      setCurrentVideoIndex(index);
      setModalOpen(true);
    } else {
      // Open in new tab if modal disabled
      window.open(videos[index].url, '_blank');
    }
  };

  const handleNextVideo = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
  };

  const handlePrevVideo = () => {
    setCurrentVideoIndex((prev) => (prev - 1 + videos.length) % videos.length);
  };

  const handleCarouselNext = () => {
    setCarouselIndex((prev) => (prev + 1) % videos.length);
  };

  const handleCarouselPrev = () => {
    setCarouselIndex((prev) => (prev - 1 + videos.length) % videos.length);
  };

  if (videos.length === 0) {
    return null;
  }

  const primaryColor = theme?.primaryColor || '#2C3E50';
  const accentColor = theme?.accentColor || '#3A9188';

  // Video Card Component
  const VideoCard = ({ video, index, featured = false }: any) => {
    const embedUrl = getVideoEmbedUrl(video.url, featured && autoplayFeatured);
    const thumbnailUrl = getVideoThumbnail(video);

    return (
      <Card
        id={`video-card-${index}`}
        sx={{
          height: '100%',
          bgcolor: '#fff',
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 12px 32px ${accentColor}33`
          }
        }}
        onClick={() => handleVideoClick(index)}
      >
        {/* Thumbnail/Embed */}
        <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
          {featured && autoplayFeatured && embedUrl ? (
            <iframe
              src={embedUrl}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <>
              <CardMedia
                component="img"
                image={thumbnailUrl}
                alt={video.title}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              {/* Play Button Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.3)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.5)'
                  }
                }}
              >
                <PlayIcon
                  sx={{
                    fontSize: 80,
                    color: '#fff',
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))'
                  }}
                />
              </Box>
            </>
          )}

          {/* Category Badge */}
          {video.category && (
            <Chip
              label={video.category}
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                bgcolor: accentColor,
                color: '#fff',
                fontWeight: 600
              }}
            />
          )}
        </Box>

        {/* Card Content */}
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: primaryColor,
              mb: 1,
              fontSize: featured ? '1.5rem' : '1.125rem'
            }}
          >
            {video.title}
          </Typography>

          {video.description && (
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 2,
                lineHeight: 1.6
              }}
            >
              {video.description}
            </Typography>
          )}

          {video.metricCallout && (
            <Chip
              label={video.metricCallout}
              size="small"
              sx={{
                bgcolor: `${accentColor}22`,
                color: accentColor,
                fontWeight: 600,
                mb: video.ctaText ? 2 : 0
              }}
            />
          )}

          {video.ctaText && video.ctaLink && (
            <Button
              variant="outlined"
              href={video.ctaLink}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              sx={{
                mt: 2,
                borderColor: accentColor,
                color: accentColor,
                '&:hover': {
                  borderColor: accentColor,
                  bgcolor: `${accentColor}11`
                }
              }}
            >
              {video.ctaText}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ py: 8, px: 3, bgcolor: '#f9f9f9' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: primaryColor,
              mb: 2
            }}
          >
            {heading}
          </Typography>
          {description && (
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {/* Grid Layout */}
        {layout === 'grid' && (
          <Grid container spacing={4}>
            {videos.map((video, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <VideoCard video={video} index={index} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Featured Layout */}
        {layout === 'featured' && videos.length > 0 && (
          <>
            {/* Featured Video */}
            <Box sx={{ mb: 4 }}>
              <VideoCard video={videos[0]} index={0} featured />
            </Box>

            {/* Grid of Other Videos */}
            {videos.length > 1 && (
              <Grid container spacing={3}>
                {videos.slice(1).map((video, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index + 1}>
                    <VideoCard video={video} index={index + 1} />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* Carousel Layout */}
        {layout === 'carousel' && (
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              <VideoCard video={videos[carouselIndex]} index={carouselIndex} featured />
            </Box>

            {/* Navigation Arrows */}
            {videos.length > 1 && (
              <>
                <IconButton
                  onClick={handleCarouselPrev}
                  sx={{
                    position: 'absolute',
                    left: { xs: 8, md: -60 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: '#fff',
                    boxShadow: 2,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 32 }} />
                </IconButton>
                <IconButton
                  onClick={handleCarouselNext}
                  sx={{
                    position: 'absolute',
                    right: { xs: 8, md: -60 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: '#fff',
                    boxShadow: 2,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 32 }} />
                </IconButton>
              </>
            )}

            {/* Carousel Dots */}
            {videos.length > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1.5,
                  mt: 3
                }}
              >
                {videos.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCarouselIndex(index)}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: carouselIndex === index ? accentColor : '#ddd',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: accentColor,
                        transform: 'scale(1.2)'
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Section CTA */}
        {sectionCtaText && sectionCtaLink && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              href={sectionCtaLink}
              target="_blank"
              size="large"
              sx={{
                bgcolor: accentColor,
                color: '#fff',
                px: 5,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: primaryColor
                }
              }}
            >
              {sectionCtaText}
            </Button>
          </Box>
        )}
      </Box>

      {/* Video Modal */}
      {showModal && (
        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#000',
              boxShadow: 24
            }
          }}
        >
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#fff',
              zIndex: 1,
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
            <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
              <iframe
                src={getVideoEmbedUrl(videos[currentVideoIndex]?.url, true) || ''}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>

            {/* Video Info */}
            <Box sx={{ p: 3, bgcolor: '#1a1a1a' }}>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                {videos[currentVideoIndex]?.title}
              </Typography>
              {videos[currentVideoIndex]?.description && (
                <Typography variant="body2" sx={{ color: '#aaa' }}>
                  {videos[currentVideoIndex].description}
                </Typography>
              )}
            </Box>

            {/* Next/Previous Buttons */}
            {videos.length > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: '#1a1a1a'
                }}
              >
                <Button
                  onClick={handlePrevVideo}
                  startIcon={<ChevronLeftIcon />}
                  sx={{ color: '#fff' }}
                >
                  Previous
                </Button>
                <Typography sx={{ color: '#aaa', alignSelf: 'center' }}>
                  {currentVideoIndex + 1} / {videos.length}
                </Typography>
                <Button
                  onClick={handleNextVideo}
                  endIcon={<ChevronRightIcon />}
                  sx={{ color: '#fff' }}
                >
                  Next
                </Button>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default VideoShowcaseBlock;